import React, { useState, useEffect } from 'react';
import { useToast } from '../../../components/Toast';
import { getImageUrl } from '../../../utils/mediaUtils';
import { 
  Newspaper, 
  Upload, 
  Clock, 
  Save,
  Eye,
  X
} from 'lucide-react';
import './FormsUnified.css';

const MAX_IMAGE_SIZE = 5 * 1024 * 1024;
const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/webp'];

export default function NewsHeroForm({ apiBase }) {
  const { toast, ToastContainer } = useToast();

  const [formData, setFormData] = useState({
    kicker: 'News & Updates',
    title: 'News & Updates',
    summary: 'Stay informed with the latest announcements, campus events, and development updates.',
    slides: [
      { image: '', order: 1 },
      { image: '', order: 2 },
      { image: '', order: 3 }
    ]
  });

  const [pendingFields, setPendingFields] = useState({
    kicker: false,
    title: false,
    summary: false
  });
  const [pendingSlides, setPendingSlides] = useState({});
  const [originalData, setOriginalData] = useState({ ...formData });

  const [loading, setLoading] = useState(true);
  const [sectionId, setSectionId] = useState(null);
  const [submitting, setSubmitting] = useState({});
  const [imageUploading, setImageUploading] = useState({});
  const [uploadProgress, setUploadProgress] = useState({});
  const [mediaMap, setMediaMap] = useState({});
  const [lightboxImage, setLightboxImage] = useState(null);
  const [brokenImages, setBrokenImages] = useState({});

  const getAuthHeaders = () => {
    let token = localStorage.getItem('token') || 
                localStorage.getItem('ph_auth_token') || 
                localStorage.getItem('auth_token') || 
                sessionStorage.getItem('token');
    if (!token) {
      try {
        const u = JSON.parse(localStorage.getItem('user') || '{}');
        if (u?.token) token = u.token;
      } catch (e) {}
    }
    return {
      'Content-Type': 'application/json',
      ...(token ? { 'Authorization': `Bearer ${token}` } : {})
    };
  };

  useEffect(() => {
    const init = async () => {
      await Promise.all([loadMediaMap(), loadSection()]);
    };
    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiBase]);

  const loadMediaMap = async () => {
    try {
      const res = await fetch(`${apiBase}/homepage/cms/media?_t=${Date.now()}`, {
        credentials: 'include',
        headers: getAuthHeaders()
      });
      if (!res.ok) return;
      const json = await res.json();
      if (json.ok && Array.isArray(json.data)) {
        const map = {};
        json.data.forEach((media) => {
          const mediaId = media.id || media.media_id;
          const mediaUrl = media.url || media.file_path || media.filepath || '';
          if (mediaId && mediaUrl) {
            map[mediaId] = mediaUrl;
            map[String(mediaId)] = mediaUrl;
            const numId = Number(mediaId);
            if (!isNaN(numId)) map[numId] = mediaUrl;
          }
        });
        setMediaMap(map);
      }
    } catch (err) {
      console.warn('[NewsHeroForm] Media fetch error:', err.message);
    }
  };

  const resolveImage = (mediaId, index) => {
    if (!mediaId || brokenImages[index]) return '';
    if (mediaMap && (mediaMap[mediaId] || mediaMap[String(mediaId)])) {
      const mapped = mediaMap[mediaId] || mediaMap[String(mediaId)];
      if (mapped.startsWith('http://') || mapped.startsWith('https://')) return mapped;
      const origin = (apiBase || '').replace(/\/api\/?$/, '');
      return origin ? `${origin}${mapped}` : mapped;
    }
    const resolved = getImageUrl(mediaId, mediaMap);
    if (resolved) {
      if (resolved.startsWith('http://') || resolved.startsWith('https://')) return resolved;
      const origin = (apiBase || '').replace(/\/api\/?$/, '');
      return origin ? `${origin}${resolved}` : resolved;
    }
    if (typeof mediaId === 'string' && (mediaId.startsWith('http://') || mediaId.startsWith('https://'))) {
      return mediaId;
    }
    if (typeof mediaId === 'string' && mediaId.startsWith('/')) {
      const origin = (apiBase || '').replace(/\/api\/?$/, '');
      return origin ? `${origin}${mediaId}` : mediaId;
    }
    return '';
  };

  const loadSection = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${apiBase}/homepage/cms/sections?page=news-updates`, {
        credentials: 'include',
        headers: getAuthHeaders()
      });

      let foundSection = null;
      if (res.ok) {
        const json = await res.json();
        const items = Array.isArray(json.data) ? json.data : Object.values(json.data || {});
        foundSection = items.find(s => 
          (s.page === 'news-updates' || s.page === 'news-and-updates' || s.page === 'news') &&
          (s.section_type === 'news-hero' || s.section_type === 'news-updates-hero')
        ) || items.find(s => s.section_type === 'news-hero' || s.section_type === 'news-updates-hero');
      }

      if (!foundSection) {
        const fbRes = await fetch(`${apiBase}/homepage/cms/sections?published=true&page=news-updates`, {
          credentials: 'include',
          headers: getAuthHeaders()
        });
        if (fbRes.ok) {
          const fbJson = await fbRes.json();
          const items = Array.isArray(fbJson.data) ? fbJson.data : Object.values(fbJson.data || {});
          foundSection = items.find(s => 
            (s.page === 'news-updates' || s.page === 'news-and-updates' || s.page === 'news') &&
            (s.section_type === 'news-hero' || s.section_type === 'news-updates-hero')
          ) || items.find(s => s.section_type === 'news-hero' || s.section_type === 'news-updates-hero');
        }
      }

      // If section is still not found, auto-ensure it exists via create-or-get endpoint
      if (!foundSection) {
        try {
          const createRes = await fetch(`${apiBase}/homepage/cms/sections/create-or-get`, {
            method: 'POST',
            credentials: 'include',
            headers: getAuthHeaders(),
            body: JSON.stringify({
              page: 'news-updates',
              section_type: 'news-hero',
              content: {
                en: {
                  kicker: 'News & Updates',
                  title: 'News & Updates',
                  summary: 'Stay informed with the latest announcements, campus events, and development updates.',
                  slides: [
                    { image: '', order: 1 },
                    { image: '', order: 2 },
                    { image: '', order: 3 }
                  ]
                }
              }
            })
          });
          if (createRes.ok) {
            const createJson = await createRes.json();
            foundSection = createJson.data;
          }
        } catch (ce) {
          console.warn('[NewsHeroForm] Auto-create section error:', ce);
        }
      }

      if (foundSection) {
        setSectionId(foundSection.id);

        let content = foundSection.content_published;
        if (!content || content === '{}' || content === '[]') {
          content = foundSection.content;
        }
        if (typeof content === 'string') {
          try {
            content = JSON.parse(content);
          } catch (e) {
            content = {};
          }
        }

        const en = content?.en || content || {};
        const rawSlides = Array.isArray(en.slides) && en.slides.length > 0 
          ? en.slides 
          : (Array.isArray(en.hero_slides) && en.hero_slides.length > 0 ? en.hero_slides : formData.slides);

        const normalizedSlides = rawSlides.slice(0, 3).map((s, idx) => {
          const imgVal = typeof s === 'object' ? (s.image || s.url || '') : (s || '');
          return {
            image: imgVal,
            order: idx + 1
          };
        });
        while (normalizedSlides.length < 3) {
          normalizedSlides.push({ image: '', order: normalizedSlides.length + 1 });
        }

        const loadedData = {
          kicker: en.kicker || 'News & Updates',
          title: en.title || 'News & Updates',
          summary: en.summary || en.subtitle || 'Stay informed with the latest announcements, campus events, and development updates.',
          slides: normalizedSlides
        };

        const pFields = { kicker: false, title: false, summary: false };
        const pSlides = {};

        try {
          const pendingRes = await fetch(`${apiBase}/cms/section/${foundSection.id}/with-pending`, {
            credentials: 'include',
            headers: getAuthHeaders()
          });
          if (pendingRes.ok) {
            const pData = await pendingRes.json();
            if (pData.success && pData.data?.pending_changes) {
              pData.data.pending_changes.forEach(c => {
                if (c.status === 'pending') {
                  let val = c.new_value;
                  if (typeof val === 'string' && (val.startsWith('{') || val.startsWith('['))) {
                    try { val = JSON.parse(val); } catch (e) {}
                  }

                  if (['kicker', 'title', 'summary'].includes(c.field_id)) {
                    pFields[c.field_id] = true;
                    if (val !== undefined && val !== null) {
                      loadedData[c.field_id] = val;
                    }
                  } else if (c.field_id?.includes('slides.')) {
                    const m = c.field_id.match(/slides\.(\d+)(?:\.(add|update|delete|image|order))?/);
                    if (m) {
                      const sIdx = parseInt(m[1], 10);
                      pSlides[`slide_${sIdx}`] = true;
                      if (val) {
                        const slideVal = typeof val === 'object' ? val : { image: val, order: sIdx + 1 };
                        if (loadedData.slides[sIdx]) {
                          loadedData.slides[sIdx] = { ...loadedData.slides[sIdx], ...slideVal };
                        } else {
                          loadedData.slides.push(slideVal);
                        }
                      }
                    }
                  }
                }
              });
            }
          }
        } catch (pe) {
          console.warn('[NewsHeroForm] Pending check error:', pe);
        }

        setFormData(loadedData);
        setOriginalData(loadedData);
        setPendingFields(pFields);
        setPendingSlides(pSlides);
      }
    } catch (err) {
      console.error('[NewsHeroForm] Load error:', err);
      toast.error('Failed to load News Hero content');
    } finally {
      setLoading(false);
    }
  };

  const handleFieldChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSlideChange = (index, field, value) => {
    setFormData(prev => {
      const next = [...prev.slides];
      next[index] = { ...next[index], [field]: value };
      return { ...prev, slides: next };
    });
  };

  const handleImageUpload = (index, e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > MAX_IMAGE_SIZE) {
      toast.error('File size must be less than 5MB');
      return;
    }
    if (!ALLOWED_TYPES.includes(file.type)) {
      toast.error('Only JPG, PNG, and WebP images are supported');
      return;
    }

    setImageUploading(prev => ({ ...prev, [index]: true }));
    setUploadProgress(prev => ({ ...prev, [index]: 0 }));

    const uploadData = new FormData();
    uploadData.append('file', file);
    uploadData.append('section_type', 'news-hero');

    const xhr = new XMLHttpRequest();
    xhr.open('POST', `${apiBase}/media/upload`);
    xhr.withCredentials = true;

    const token = localStorage.getItem('token') || 
                  localStorage.getItem('ph_auth_token') || 
                  localStorage.getItem('auth_token') || 
                  sessionStorage.getItem('token');
    if (token) {
      xhr.setRequestHeader('Authorization', `Bearer ${token}`);
    }

    xhr.upload.onprogress = (ev) => {
      if (ev.lengthComputable) {
        const pct = Math.round((ev.loaded / ev.total) * 100);
        setUploadProgress(prev => ({ ...prev, [index]: pct }));
      }
    };

    xhr.onload = () => {
      setImageUploading(prev => ({ ...prev, [index]: false }));
      if (xhr.status === 200 || xhr.status === 201) {
        try {
          const res = JSON.parse(xhr.responseText);
          const data = res.data || res;
          const mediaId = data.media_id || data.id || res.media_id || res.id;
          const returnedUrl = data.url || data.file_path || data.path || res.url || (data.filename ? `/api/uploads/images/${data.filename}` : '');
          const origin = (apiBase || '').replace(/\/api\/?$/, '');
          const fullUrl = returnedUrl && (returnedUrl.startsWith('http://') || returnedUrl.startsWith('https://'))
            ? returnedUrl
            : (returnedUrl ? `${origin}${returnedUrl.startsWith('/') ? '' : '/'}${returnedUrl}` : '');
          const finalUrl = fullUrl || returnedUrl || String(mediaId || '');

          if (finalUrl) {
            handleSlideChange(index, 'image', finalUrl);
            setBrokenImages(prev => ({ ...prev, [index]: false }));
            if (mediaId) {
              setMediaMap(prev => ({
                ...prev,
                [mediaId]: finalUrl,
                [String(mediaId)]: finalUrl,
                [finalUrl]: finalUrl
              }));
            }
            toast.success('Foto slide berhasil diupload');
          } else {
            toast.error('Gagal membaca URL gambar dari server');
          }
        } catch (err) {
          toast.error('Gagal memproses respon upload');
        }
      } else {
        toast.error(`Upload gagal (${xhr.status})`);
      }
    };

    xhr.onerror = () => {
      setImageUploading(prev => ({ ...prev, [index]: false }));
      toast.error('Terjadi gangguan jaringan saat upload');
    };

    xhr.send(uploadData);
  };

  const handleSubmitField = async (field) => {
    if (!sectionId) {
      toast.error('Section ID not found');
      return;
    }

    if (formData[field] === originalData[field]) {
      toast.warning('No changes detected for this field');
      return;
    }

    setSubmitting(prev => ({ ...prev, [field]: true }));
    try {
      const res = await fetch(`${apiBase}/cms/field-change/submit`, {
        method: 'POST',
        headers: getAuthHeaders(),
        credentials: 'include',
        body: JSON.stringify({
          section_id: sectionId,
          field_id: field,
          field_name: field.charAt(0).toUpperCase() + field.slice(1),
          old_value: originalData[field] || '',
          new_value: formData[field],
          locale: 'en'
        })
      });

      const result = await res.json();
      if (result.success || result.ok) {
        toast.success(`"${field}" submitted for approval`);
        setPendingFields(prev => ({ ...prev, [field]: true }));
        setOriginalData(prev => ({ ...prev, [field]: formData[field] }));
        try {
          sessionStorage.removeItem('cms_page_news-updates');
          sessionStorage.removeItem('cms_page_news-and-updates');
          sessionStorage.removeItem('cms_sections_news-updates');
        } catch (e) {}
      } else {
        toast.error(result.error || result.message || 'Submission failed');
      }
    } catch (err) {
      console.error('[NewsHeroForm] Submit error:', err);
      toast.error('Error submitting change: ' + err.message);
    } finally {
      setSubmitting(prev => ({ ...prev, [field]: false }));
    }
  };

  const handleSubmitSlide = async (index) => {
    if (!sectionId) {
      toast.error('Section ID not found');
      return;
    }

    const slide = formData.slides[index];
    if (!slide?.image || brokenImages[index]) {
      toast.warning('Silakan upload foto slide terlebih dahulu sebelum submit');
      return;
    }

    if (originalData.slides?.[index]?.image === slide.image && !pendingSlides[`slide_${index}`]) {
      toast.warning('Tidak ada perubahan pada foto slide ini');
      return;
    }

    const key = `slide_${index}`;
    setSubmitting(prev => ({ ...prev, [key]: true }));

    try {
      const res = await fetch(`${apiBase}/cms/field-change/submit`, {
        method: 'POST',
        headers: getAuthHeaders(),
        credentials: 'include',
        body: JSON.stringify({
          section_id: sectionId,
          field_id: `slides.${index}.update`,
          field_name: `Hero Slide #${index + 1}`,
          old_value: originalData.slides?.[index] || { image: '', order: index + 1 },
          new_value: { image: slide.image, order: index + 1 },
          locale: 'en'
        })
      });

      const result = await res.json();
      if (result.success || result.ok) {
        toast.success(`Slide #${index + 1} submitted for approval`);
        setPendingSlides(prev => ({ ...prev, [key]: true }));
        setOriginalData(prev => {
          const next = [...(prev.slides || [])];
          next[index] = slide;
          return { ...prev, slides: next };
        });
        try {
          sessionStorage.removeItem('cms_page_news-updates');
          sessionStorage.removeItem('cms_page_news-and-updates');
          sessionStorage.removeItem('cms_sections_news-updates');
        } catch (e) {}
      } else {
        toast.error(result.error || result.message || 'Submission failed');
      }
    } catch (err) {
      console.error('[NewsHeroForm] Slide submit error:', err);
      toast.error('Error submitting slide: ' + err.message);
    } finally {
      setSubmitting(prev => ({ ...prev, [key]: false }));
    }
  };

  if (loading) {
    return (
      <div className="unified-loading-container">
        <div className="unified-loading-spinner" />
        <p className="unified-loading-text">Loading News Hero section...</p>
      </div>
    );
  }

  return (
    <div className="unified-form-container">
      <ToastContainer />

      {/* Lightbox Modal */}
      {lightboxImage && (
        <div 
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(0,0,0,0.85)',
            zIndex: 99999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '20px'
          }}
          onClick={() => setLightboxImage(null)}
        >
          <div style={{ position: 'relative', maxWidth: '90vw', maxHeight: '90vh' }}>
            <img 
              src={lightboxImage} 
              alt="Preview" 
              style={{ maxWidth: '100%', maxHeight: '90vh', borderRadius: '12px', objectFit: 'contain' }}
            />
            <button
              type="button"
              onClick={() => setLightboxImage(null)}
              style={{
                position: 'absolute',
                top: '-14px',
                right: '-14px',
                background: '#ffffff',
                border: 'none',
                borderRadius: '50%',
                width: '32px',
                height: '32px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                boxShadow: '0 4px 12px rgba(0,0,0,0.3)'
              }}
            >
              <X size={18} color="#0f172a" />
            </button>
          </div>
        </div>
      )}

      {/* Form Header */}
      <div className="unified-header">
        <div className="unified-header-icon">
          <Newspaper size={22} />
        </div>
        <div>
          <h2 className="unified-title">News & Updates Hero Header</h2>
          <p className="unified-subtitle">
            Kelola judul utama, kicker, deskripsi, dan foto carousel slideshow halaman News & Updates (Maksimal 3 Foto - Hanya Ganti Foto).
          </p>
        </div>
      </div>

      {/* Field: Kicker */}
      <div className="unified-field-card">
        <div className="unified-field-header">
          <label className="unified-label">Section Kicker</label>
          {pendingFields.kicker && (
            <span className="unified-pending-pill">
              <Clock size={12} /> Pending Review
            </span>
          )}
        </div>
        <div className="unified-input-action-row">
          <input
            type="text"
            className="unified-input"
            value={formData.kicker}
            onChange={(e) => handleFieldChange('kicker', e.target.value)}
            disabled={pendingFields.kicker || submitting.kicker}
            placeholder="e.g. News & Updates"
          />
          <button
            type="button"
            className="unified-submit-button"
            onClick={() => handleSubmitField('kicker')}
            disabled={pendingFields.kicker || submitting.kicker || formData.kicker === originalData.kicker}
          >
            <Save size={14} /> {submitting.kicker ? 'Saving...' : 'Save & Submit'}
          </button>
        </div>
      </div>

      {/* Field: Title */}
      <div className="unified-field-card">
        <div className="unified-field-header">
          <label className="unified-label">Main Title</label>
          {pendingFields.title && (
            <span className="unified-pending-pill">
              <Clock size={12} /> Pending Review
            </span>
          )}
        </div>
        <div className="unified-input-action-row">
          <input
            type="text"
            className="unified-input"
            value={formData.title}
            onChange={(e) => handleFieldChange('title', e.target.value)}
            disabled={pendingFields.title || submitting.title}
            placeholder="e.g. News & Updates"
          />
          <button
            type="button"
            className="unified-submit-button"
            onClick={() => handleSubmitField('title')}
            disabled={pendingFields.title || submitting.title || formData.title === originalData.title}
          >
            <Save size={14} /> {submitting.title ? 'Saving...' : 'Save & Submit'}
          </button>
        </div>
      </div>

      {/* Field: Summary */}
      <div className="unified-field-card">
        <div className="unified-field-header">
          <label className="unified-label">Summary / Description</label>
          {pendingFields.summary && (
            <span className="unified-pending-pill">
              <Clock size={12} /> Pending Review
            </span>
          )}
        </div>
        <div className="unified-input-action-row" style={{ alignItems: 'flex-start' }}>
          <textarea
            className="unified-textarea"
            rows={3}
            value={formData.summary}
            onChange={(e) => handleFieldChange('summary', e.target.value)}
            disabled={pendingFields.summary || submitting.summary}
            placeholder="Ringkasan atau pengantar pada header..."
          />
          <button
            type="button"
            className="unified-submit-button"
            style={{ marginTop: '2px' }}
            onClick={() => handleSubmitField('summary')}
            disabled={pendingFields.summary || submitting.summary || formData.summary === originalData.summary}
          >
            <Save size={14} /> {submitting.summary ? 'Saving...' : 'Save & Submit'}
          </button>
        </div>
      </div>

      {/* Hero Slides Carousel Cards */}
      <div className="unified-field-card">
        <div className="unified-card-header">
          <div>
            <label className="unified-field-label">Hero Slides Carousel (3 Slides)</label>
            <p className="unified-field-desc">
              Slideshow foto banner pada bagian atas halaman News & Updates (Maksimal 3 Foto - Hanya Ganti Foto).
            </p>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '20px', marginTop: '16px' }}>
          {formData.slides.map((slide, idx) => {
            const isPending = pendingSlides[`slide_${idx}`];
            const isSubmitting = submitting[`slide_${idx}`];
            const isUploading = imageUploading[idx];
            const previewUrl = resolveImage(slide.image, idx);
            const isSubmitDisabled = isPending || isSubmitting || !slide.image || brokenImages[idx];

            return (
              <div 
                key={idx} 
                style={{ 
                  border: isPending ? '2px solid #f59e0b' : '1px solid #e2e8f0', 
                  borderRadius: '14px', 
                  background: isPending ? '#fffdfa' : '#ffffff',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
                  overflow: 'hidden',
                  display: 'flex',
                  flexDirection: 'column',
                  transition: 'all 0.2s ease'
                }}
              >
                {/* Photo Dropzone / Preview */}
                <div 
                  style={{ 
                    height: '160px', 
                    background: '#0f172a',
                    position: 'relative',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    overflow: 'hidden'
                  }}
                >
                  {previewUrl ? (
                    <>
                      <img 
                        src={previewUrl} 
                        alt={`Slide ${idx + 1}`} 
                        style={{ width: '100%', height: '100%', objectFit: 'cover', cursor: 'pointer' }}
                        onClick={() => setLightboxImage(previewUrl)}
                        onError={() => setBrokenImages(prev => ({ ...prev, [idx]: true }))}
                      />
                      <button
                        type="button"
                        onClick={() => setLightboxImage(previewUrl)}
                        style={{
                          position: 'absolute',
                          bottom: '8px',
                          right: '8px',
                          background: 'rgba(0,0,0,0.65)',
                          border: 'none',
                          borderRadius: '6px',
                          color: '#fff',
                          padding: '4px 8px',
                          cursor: 'pointer',
                          fontSize: '11px',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '4px'
                        }}
                        title="Lihat Foto"
                      >
                        <Eye size={13} />
                      </button>
                    </>
                  ) : (
                    <label 
                      style={{
                        width: '100%',
                        height: '100%',
                        background: '#f8fafc',
                        borderBottom: '1px solid #e2e8f0',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: isPending || isUploading ? 'not-allowed' : 'pointer',
                        gap: '6px',
                        padding: '16px',
                        textAlign: 'center',
                        transition: 'all 0.2s ease'
                      }}
                    >
                      <div style={{ background: '#e2e8f0', borderRadius: '50%', padding: '10px', display: 'flex' }}>
                        <Upload size={20} color="#64748b" />
                      </div>
                      <span style={{ fontSize: '13px', fontWeight: 600, color: '#334155' }}>
                        {isUploading ? `Mengupload (${uploadProgress[idx] || 0}%)...` : 'Upload Foto Slide'}
                      </span>
                      {isUploading && (
                        <div style={{ width: '80%', height: '6px', background: '#e2e8f0', borderRadius: '3px', overflow: 'hidden', marginTop: '6px' }}>
                          <div style={{ width: `${uploadProgress[idx] || 0}%`, height: '100%', background: '#3b82f6', transition: 'width 0.2s ease' }} />
                        </div>
                      )}
                      <span style={{ fontSize: '11px', color: '#94a3b8' }}>JPG, PNG, WebP (Maks 5MB)</span>
                      <input 
                        type="file" 
                        accept="image/*" 
                        onChange={(e) => handleImageUpload(idx, e)}
                        disabled={isPending || isUploading}
                        style={{ display: 'none' }}
                      />
                    </label>
                  )}
                </div>

                {/* Card Content & Footer */}
                <div style={{ padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: '12px', flex: 1, justifyContent: 'space-between' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span style={{ fontWeight: 700, color: '#0f172a', fontSize: '14px' }}>Slide #{idx + 1}</span>
                      {isPending && (
                        <span className="unified-pending-pill" style={{ padding: '2px 8px', fontSize: '10.5px' }}>
                          <Clock size={11} /> Pending Review
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Actions: Change Photo & Save & Submit */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginTop: 'auto' }}>
                    {previewUrl && (
                      <label 
                        className="unified-secondary-btn"
                        style={{ 
                          cursor: isPending || isUploading ? 'not-allowed' : 'pointer',
                          justifyContent: 'center',
                          fontSize: '12px',
                          padding: '7px 12px'
                        }}
                      >
                        <Upload size={13} />
                        {isUploading ? `Mengupload (${uploadProgress[idx] || 0}%)...` : 'Ganti Foto'}
                        <input 
                          type="file" 
                          accept="image/*" 
                          onChange={(e) => handleImageUpload(idx, e)}
                          disabled={isPending || isUploading}
                          style={{ display: 'none' }}
                        />
                      </label>
                    )}

                    <button
                      type="button"
                      className="unified-submit-button"
                      style={{ width: '100%', justifyContent: 'center', padding: '8px 14px', fontSize: '13px' }}
                      onClick={() => handleSubmitSlide(idx)}
                      disabled={isSubmitDisabled}
                    >
                      <Save size={14} /> {isSubmitting ? 'Menyimpan...' : 'Save & Submit Slide'}
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
